import { TFunction } from 'i18next'

import { DOUGHNUT_CHART_COLORS, getDoughnutSubtypeLabels } from '@/constants'
import { SurveyQuestion, SurveyQuestionResponse } from '@/models'
import { DefaultColors } from '@/theme/defaults/colors'

type LegacyChartData = {
  datasets: {
    backgroundColor: string[]
    borderColor: string[]
    borderWidth: number
    data: number[]
  }[]
  labels: string[]
}

type LegacyChartOptions = {
  cutoutPercentage: number
  maintainAspectRatio: boolean
  legend: {
    align?: string
    display?: boolean
    labels?: { boxWidth: number; fontSize: number }
    position?: string
  }
  plugins: {
    datalabels: { display: boolean }
  }
  tooltips: {
    backgroundColor: string
    bodyFontColor: string
    bodyFontSize: number
    borderColor: string
    borderWidth: number
    callbacks: {
      label: (tooltipItem: { datasetIndex: number; index: number }, data: LegacyChartData) => string
    }
    cornerRadius: number
  }
}

export const getTotalResponses = (responses: SurveyQuestionResponse[]): number =>
  responses.reduce((sum, response) => sum + response.count, 0)

export const getPercentageLabel = (count: number, total: number): string => {
  const percentage = total ? ((count / total) * 100).toFixed(1) : '0.0'
  return `${percentage}% (${count})`
}

export const getChartLabels = (
  question: SurveyQuestion,
  subtype: string | undefined,
  t: TFunction<['communication']>,
): string[] => {
  const doughnutSubtypeLabels = getDoughnutSubtypeLabels(t)
  if (subtype && doughnutSubtypeLabels[subtype]) return doughnutSubtypeLabels[subtype]
  return question.answerOptions ?? []
}

export const getChartData = (
  question: SurveyQuestion,
  subtype: string | undefined,
  t: TFunction<['communication']>,
): LegacyChartData => {
  const responses = question.responses ?? []
  const labels = getChartLabels(question, subtype, t)

  return {
    datasets: [
      {
        backgroundColor: [...DOUGHNUT_CHART_COLORS],
        borderColor: [...DOUGHNUT_CHART_COLORS],
        borderWidth: 1,
        data: responses.map((r) => r.count),
      },
    ],
    labels,
  }
}

export const getChartOptions = (totalResponses: number): LegacyChartOptions => ({
  cutoutPercentage: 60,
  legend: {
    display: false,
  },
  maintainAspectRatio: false,
  plugins: {
    datalabels: { display: false },
  },
  tooltips: {
    backgroundColor: 'rgba(255, 255, 255, 0.80)',
    bodyFontColor: DefaultColors.black800,
    bodyFontSize: 18,
    borderColor: DefaultColors.gray400,
    borderWidth: 1,
    callbacks: {
      label: (tooltipItem, data) => {
        const count = Number(data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index] ?? 0)

        return ` ${getPercentageLabel(count, totalResponses)}`
      },
    },
    cornerRadius: 6,
  },
})
